import React from 'react';
import { isEditor } from '../../utils/isEditor.js';
import { FieldConfig, DISCOUNT_POINTS } from './ClosingCostCalculator.utils.js';
import ClosingCostCalculator from './ClosingCostCalculator.js';

var _attributes;
var ClosingCostCalculatorBlock = {
  Component: function Component(_ref) {
    var attrs = _ref.attrs;
    var homePrice = attrs.homePrice,
      downPayment = attrs.downPayment,
      interestRate = attrs.interestRate,
      purchasingAnyDiscountPoints = attrs.purchasingAnyDiscountPoints,
      downPaymentPercent = attrs.downPaymentPercent,
      discountPoints = attrs.discountPoints,
      creditReportFee = attrs.creditReportFee,
      titleSearchFee = attrs.titleSearchFee,
      attorneyFees = attrs.attorneyFees,
      appraisalFee = attrs.appraisalFee,
      homeInspectionFee = attrs.homeInspectionFee,
      pestInspectionFee = attrs.pestInspectionFee,
      surveyFee = attrs.surveyFee,
      recordingFee = attrs.recordingFee,
      courierFee = attrs.courierFee,
      notaryFee = attrs.notaryFee,
      hoaTransferFee = attrs.hoaTransferFee,
      floodCertificationFee = attrs.floodCertificationFee,
      bankProcessingFee = attrs.bankProcessingFee,
      propertyTaxes = attrs.propertyTaxes,
      loanOriginationFee = attrs.loanOriginationFee,
      titleInsurance = attrs.titleInsurance,
      settelementOrEscrowFee = attrs.settelementOrEscrowFee,
      homeownerInsurance = attrs.homeownerInsurance,
      prepaidInterest = attrs.prepaidInterest;
    var defaultValues = {
      homePrice: homePrice,
      downPayment: downPayment,
      downPaymentPercent: downPaymentPercent,
      interestRate: interestRate,
      purchasingAnyDiscountPoints: purchasingAnyDiscountPoints,
      discountPoints: discountPoints,
      creditReportFee: creditReportFee,
      titleSearchFee: titleSearchFee,
      attorneyFees: attorneyFees,
      appraisalFee: appraisalFee,
      homeInspectionFee: homeInspectionFee,
      pestInspectionFee: pestInspectionFee,
      surveyFee: surveyFee,
      recordingFee: recordingFee,
      courierFee: courierFee,
      notaryFee: notaryFee,
      hoaTransferFee: hoaTransferFee,
      floodCertificationFee: floodCertificationFee,
      bankProcessingFee: bankProcessingFee,
      propertyTaxes: propertyTaxes,
      loanOriginationFee: loanOriginationFee,
      titleInsurance: titleInsurance,
      settelementOrEscrowFee: settelementOrEscrowFee,
      homeownerInsurance: homeownerInsurance,
      prepaidInterest: prepaidInterest
    };
    return /*#__PURE__*/React.createElement(ClosingCostCalculator, {
      key: isEditor() ? JSON.stringify(defaultValues) : undefined,
      defaultValues: defaultValues
    });
  },
  metadata: {
    version: '1.0',
    name: 'constellation/closing-cost-calculator',
    title: 'Closing Cost Calculator',
    description: "Zillow's closing cost calculator helps users estimate the fees they'll pay at closing on a home purchase.",
    icon: 'calculator',
    keywords: ['mortgages', 'calculators', 'closing', 'cost'],
    supports: {
      customClassName: false,
      html: false
    },
    category: 'Calculators',
    attributes: (_attributes = {}, _attributes[FieldConfig.homePrice.name] = {
      type: 'number',
      default: 400000,
      label: 'Default home price',
      control: 'number'
    }, _attributes[FieldConfig.downPayment.name] = {
      type: 'number',
      default: 80000,
      label: 'Default downpayment',
      control: 'number'
    }, _attributes[FieldConfig.interestRate.name] = {
      type: 'number',
      label: 'Default interest rate',
      default: 4.176,
      control: 'number'
    }, _attributes[FieldConfig.downPaymentPercent.name] = {
      type: 'number',
      label: 'Default downpayment percent',
      default: 20,
      control: 'number'
    }, _attributes[FieldConfig.purchasingAnyDiscountPoints.name] = {
      type: 'string',
      default: DISCOUNT_POINTS.defaultPoint,
      label: 'Default purchasing discount points',
      control: 'select',
      choices: FieldConfig.purchasingAnyDiscountPoints.options
    }, _attributes[FieldConfig.loanOriginationFee.name] = {
      type: 'number',
      default: 1600,
      label: 'Loan fees: Default loan origination fee',
      control: 'number'
    }, _attributes[FieldConfig.discountPoints.name] = {
      type: 'number',
      default: 0,
      label: 'Loan fees: Default discount points',
      control: 'number'
    }, _attributes[FieldConfig.creditReportFee.name] = {
      type: 'number',
      default: 35,
      label: 'Loan fees: Default credit report fee',
      control: 'number'
    }, _attributes[FieldConfig.titleSearchFee.name] = {
      type: 'number',
      default: 2000,
      label: 'Title and legal fees: Default title search fee',
      control: 'number'
    }, _attributes[FieldConfig.attorneyFees.name] = {
      type: 'number',
      default: 500,
      label: 'Title and legal fees: Default attorney fee',
      control: 'number'
    }, _attributes[FieldConfig.titleInsurance.name] = {
      type: 'number',
      default: 1000,
      label: 'Title and legal fees: Default title insurance fee',
      control: 'number'
    }, _attributes[FieldConfig.settelementOrEscrowFee.name] = {
      type: 'number',
      default: 1000,
      label: 'Title and legal fees: Default settelement or escrow fee',
      control: 'number'
    }, _attributes[FieldConfig.homeownerInsurance.name] = {
      type: 'number',
      default: 1400,
      label: 'Prepaid expenses: Default homeowner insurance fee',
      control: 'number'
    }, _attributes[FieldConfig.propertyTaxes.name] = {
      type: 'number',
      default: 315,
      label: 'Prepaid expenses: Default property taxes fee',
      control: 'number'
    }, _attributes[FieldConfig.prepaidInterest.name] = {
      type: 'number',
      default: 789,
      label: 'Prepaid expenses: Default prepaid interest fee',
      control: 'number'
    }, _attributes[FieldConfig.appraisalFee.name] = {
      type: 'number',
      default: 500,
      label: 'Inspection and appraisal fees: Default appraisal fee',
      control: 'number'
    }, _attributes[FieldConfig.homeInspectionFee.name] = {
      type: 'number',
      default: 400,
      label: 'Inspection and appraisal fees: Default home inspection fee',
      control: 'number'
    }, _attributes[FieldConfig.pestInspectionFee.name] = {
      type: 'number',
      default: 100,
      label: 'Inspection and appraisal fees: Default pest inspection fee',
      control: 'number'
    }, _attributes[FieldConfig.surveyFee.name] = {
      type: 'number',
      default: 500,
      label: 'Inspection and appraisal fees: Default survey fee',
      control: 'number'
    }, _attributes[FieldConfig.recordingFee.name] = {
      type: 'number',
      default: 150,
      label: 'Government fees: Default recording fee',
      control: 'number'
    }, _attributes[FieldConfig.courierFee.name] = {
      type: 'number',
      default: 20,
      label: 'Additional fees: Default courier fee',
      control: 'number'
    }, _attributes[FieldConfig.notaryFee.name] = {
      type: 'number',
      default: 100,
      label: 'Additional fees: Default notary fee',
      control: 'number'
    }, _attributes[FieldConfig.hoaTransferFee.name] = {
      type: 'number',
      default: 0,
      label: 'Additional fees: Default HOA transfer fee',
      control: 'number'
    }, _attributes[FieldConfig.floodCertificationFee.name] = {
      type: 'number',
      default: 15,
      label: 'Additional fees: Default flood certification fee',
      control: 'number'
    }, _attributes[FieldConfig.bankProcessingFee.name] = {
      type: 'number',
      default: 50,
      label: 'Additional fees: Default bank processing fee',
      control: 'number'
    }, _attributes)
  }
};
var ClosingCostCalculatorBlock$1 = ClosingCostCalculatorBlock;

export { ClosingCostCalculatorBlock$1 as default };
